/**
 * Deploy 5 · C3 — Bandeja "Solicitudes pendientes" (owner): píldora sobre la agenda +
 * bottom-sheet con Aprobar / Rechazar. Sin auto-expiración: lo vencido queda atenuado.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Clock, Sparkles, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { bookingApi } from '@/lib/bookingApi';
import { formatDateShortEs, formatTime12 } from '@/utils/bookingFormat';
import { onBookingUpdated, onWindowRefocus } from '@/lib/bookingEvents';
import { tr, localeTag } from '@/contexts/LanguageContext';
import { fmtMoney } from '@/utils/money';

const money = (v) => fmtMoney(v);

export const usePremiumRequests = (enabled) => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    if (!enabled) return;
    try { setData(await bookingApi.premiumRequests()); } catch { /* silencioso */ }
  }, [enabled]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!enabled) return undefined;
    const off1 = onBookingUpdated(load);
    const off2 = onWindowRefocus(load);
    const iv = setInterval(load, 60000);
    return () => { off1(); off2(); clearInterval(iv); };
  }, [enabled, load]);
  return { data, reload: load };
};

const CONFLICT_LABEL = {
  occupied: tr("Choca con una cita"),
  absence: tr("Choca con tu ausencia"),
  day_closed: tr("Ese día ya no atiendes"),
};
const CONFLICT_REASON = tr("el horario fue tomado");

const RequestCard = ({ r, busy, onApprove, onReject }) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const base = Number(r.base_price ?? r.price);
  const sc = Number(r.premium?.surcharge || 0);
  const conflict = Boolean(r.conflict);
  return (
    <div className={`bg-background border rounded-xl p-3 space-y-2 ${r.expired_time ? 'border-border opacity-60' : conflict ? 'border-destructive/50' : 'border-primary/40'}`} data-testid={`premium-request-${r.id}`} data-conflict={conflict ? 'true' : undefined}>
      <div className="flex items-center gap-3">
        <Avatar className="w-9 h-9 border border-primary/30">
          <AvatarImage src={r.client_avatar} alt={r.client_name} />
          <AvatarFallback>{(r.client_name || '?')[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate" data-testid={`premium-request-client-${r.id}`}>{r.client_name}</p>
          <p className="text-xs text-muted-foreground truncate">{r.service_name} · {formatDateShortEs(r.date)} · {formatTime12(r.start_time)}–{formatTime12(r.end_time)}</p>
        </div>
        {r.expired_time && <span className="text-[9px] uppercase tracking-wider text-muted-foreground border border-border rounded-full px-2 py-0.5" data-testid={`premium-request-expired-${r.id}`}>{tr("Vencida")}</span>}
      </div>
      {conflict && !r.expired_time && (
        <p className="text-xs font-semibold text-destructive inline-flex items-center gap-1.5" data-testid={`premium-request-conflict-${r.id}`}>
          <AlertTriangle className="w-3.5 h-3.5" />{' '}{tr("{0} — ya no se puede aprobar", CONFLICT_LABEL[r.conflict_reason] || CONFLICT_LABEL.occupied)}</p>
      )}
      <p className="text-xs text-foreground/85 tabular-nums" data-testid={`premium-request-total-${r.id}`}>
        {money(base)} + <span className="text-primary">{tr("{0} fuera de horario ({1})", money(sc), r.premium?.label)}</span> = <b className="text-primary">{money(r.total_amount ?? r.price)}</b>
      </p>
      <p className="text-[10px] text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Solicitada {new Date(r.premium?.requested_at || r.created_at).toLocaleString(localeTag(), { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
      {rejecting ? (
        <div className="flex items-center gap-2">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={conflict ? CONFLICT_REASON : tr("Motivo (opcional)")} maxLength={200} className="h-9 rounded-lg bg-card border-border text-sm" data-testid={`premium-reject-reason-${r.id}`} />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onReject(r, reason || (conflict ? CONFLICT_REASON : ''))} className="h-9 rounded-full border-destructive/50 text-destructive text-xs" data-testid={`premium-reject-confirm-${r.id}`}>{tr("Rechazar")}</Button>
          <Button size="sm" variant="ghost" onClick={() => setRejecting(false)} className="h-9 rounded-full text-xs">{tr("Volver")}</Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {!r.expired_time && !conflict && (
            <Button size="sm" disabled={busy} onClick={() => onApprove(r)} className="flex-1 h-9 rounded-full bg-emerald-500 hover:bg-emerald-500/90 text-black font-semibold text-xs gap-1" data-testid={`premium-approve-${r.id}`}>
              <Check className="w-3.5 h-3.5" />{' '}{tr("Aprobar")}</Button>
          )}
          <Button
            size="sm"
            variant={conflict ? 'default' : 'outline'}
            disabled={busy}
            onClick={() => setRejecting(true)}
            className={`flex-1 h-9 rounded-full text-xs gap-1 ${conflict ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold' : 'border-destructive/50 text-destructive hover:bg-destructive/10'}`}
            data-testid={`premium-reject-${r.id}`}
          >
            <X className="w-3.5 h-3.5" /> {conflict ? tr("Rechazar y avisar al cliente") : tr("Rechazar")}
          </Button>
        </div>
      )}
    </div>
  );
};

export const PremiumRequestsBar = ({ data, reload, onChanged, autoOpen = false }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (autoOpen) setOpen(true); }, [autoOpen]);
  const items = data?.items || [];
  const pending = data?.pending_count || 0;
  const awaiting = data?.awaiting_payment || [];
  if (!items.length && !awaiting.length) return null;

  const approve = async (r) => {
    setBusy(true);
    try {
      const res = await bookingApi.premiumApprove(r.id);
      toast.success(tr("Aprobada — {0} tiene 30 min para pagar {1}", r.client_name, money(res?.total)));
      await reload(); onChanged?.();
    } catch (e) { toast.error(e?.message || tr("No se pudo aprobar")); } finally { setBusy(false); }
  };
  const reject = async (r, reason) => {
    setBusy(true);
    try {
      await bookingApi.premiumReject(r.id, reason);
      toast.success(tr("Solicitud rechazada — avisamos al cliente"));
      await reload(); onChanged?.();
    } catch (e) { toast.error(e?.message || tr("No se pudo rechazar")); } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="w-full mb-3 flex items-center gap-2 rounded-full border border-primary/60 bg-primary/10 px-4 py-2 text-left hover:bg-primary/15 transition-colors"
        data-testid="premium-requests-pill">
        <span className={`w-2 h-2 rounded-full bg-primary ${pending ? 'animate-pulse' : ''}`} />
        <Sparkles className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-primary flex-1" data-testid="premium-requests-count">
          {pending ? tr("{0} solicitud{1} premium", pending, pending === 1 ? '' : 'es') : `${items.length} solicitud${items.length === 1 ? '' : 'es'} vencida${items.length === 1 ? '' : 's'}`}
          {awaiting.length ? tr("· {0} esperando pago", awaiting.length) : ''}
        </span>
        <span className="text-xs text-primary/80">{tr("Ver")}</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" aria-describedby={undefined} className="bg-card border-border rounded-t-2xl max-h-[85vh] overflow-y-auto" data-testid="premium-requests-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-lg text-foreground flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />{' '}{tr("Solicitudes pendientes")}</SheetTitle>
            <p className="text-xs text-muted-foreground">{tr("Citas fuera de horario que piden tu aprobación. Al aprobar, el cliente tiene 30 minutos para pagar el total; si no paga, el horario vuelve a quedar libre.")}</p>
          </SheetHeader>
          <div className="mt-3 space-y-2.5">
            {items.map((r) => <RequestCard key={r.id} r={r} busy={busy} onApprove={approve} onReject={reject} />)}
            {awaiting.length > 0 && (
              <div className="pt-2" data-testid="premium-awaiting-payment">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">{tr("Aprobadas · esperando pago")}</p>
                {awaiting.map((b) => (
                  <p key={b.id} className="text-xs text-foreground/80 py-1 border-b border-border/60 last:border-0" data-testid={`premium-awaiting-${b.id}`}>
                    {b.client_name} · {formatDateShortEs(b.date)} {formatTime12(b.start_time)} · {money(b.total_amount)} · vence {new Date(b.payment_expires_at).toLocaleTimeString(localeTag(), { hour: '2-digit', minute: '2-digit' })}
                  </p>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
